"use client";

import { Plus } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const faqs = [{
  question: "What is your return policy?",
  answer: "We offer a 30-day return policy for all unworn, unwashed items with original tags attached. Simply initiate a return through your account dashboard, and we will provide a prepaid shipping label for your convenience."
}, {
  question: "How do I track my order?",
  answer: "Once your order ships, you will receive an email with a tracking number. You can also view real-time status updates by visiting our Order Tracking page at any time."
}, {
  question: "Do you offer international shipping?",
  answer: "Yes, we ship to over 50 countries worldwide. Shipping costs and estimated delivery times are calculated at checkout based on your destination address."
}, {
  question: "How can I update my account details?",
  answer: "You can manage your personal information, saved addresses, and order history by logging into the My Account section of our website."
}, {
  question: "Are your products sustainable?",
  answer: "Sustainability is at the heart of Lush Fashion. We partner with ethical manufacturers and use eco-friendly materials to ensure our collections are as kind to the planet as they are to your wardrobe."
}, {
  question: "What payment methods do you accept?",
  answer: "We accept all major credit cards, PayPal, and Apple Pay. All transactions are processed through a secure, encrypted gateway to ensure your data remains protected."
}, {
  question: "Can I change my order after placing it?",
  answer: "We process orders quickly to ensure fast delivery. If you need to make changes, please contact our customer care team within 2 hours of placing your order, and we will do our best to assist you."
}, {
  question: "How do I find my perfect size?",
  answer: "Each product page features a detailed size guide with measurements. If you are between sizes, we recommend checking our fit notes or contacting our style consultants for personalized advice."
}];
export default function Faq() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="2463" id="returnsandpolicygrid" className="bg-background text-foreground py-24 md:py-32 lg:py-40 px-6 md:px-10 lg:px-16">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div ref={leftRef} className={`lg:col-span-5 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="lg:sticky lg:top-24">
              <div className="font-default text-xs tracking-widest uppercase text-muted-foreground mb-8">
                CUSTOMER SUPPORT / 03
              </div>

              <h2 className="font-default font-bold text-foreground mb-10" style={{
              fontSize: "clamp(2.75rem, 5vw, 5rem)",
              lineHeight: "0.95",
              letterSpacing: "-0.03em"
            }}>
                Common
                <br />
                Questions
                <br />
                Answered.
              </h2>

              <p className="font-default text-base md:text-lg leading-relaxed mb-10" style={{
              maxWidth: "360px",
              color: "var(--muted-foreground)"
            }}>
                We are here to help you with your shopping experience. If you cannot find the answer you need, our team is ready to assist you.
              </p>

              <Button variant="outline" size="lg" className="font-default font-medium tracking-wider uppercase text-xs border-primary text-primary bg-transparent hover:bg-primary hover:text-primary-foreground transition-all duration-300 px-8 py-6 h-auto rounded-md">
                Contact Support
              </Button>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-1000 ease-out delay-200 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Accordion type="single" collapsible defaultValue="item-0" className="flex flex-col gap-3">
              {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="border-0 transition-all duration-300 group rounded-md" style={{
              backgroundColor: "var(--secondary)",
              border: "1px solid var(--border)"
            }}>
                  <AccordionTrigger className="hover:no-underline px-6 md:px-8 py-6 md:py-7 min-h-[96px] gap-6 [&>svg]:hidden">
                    <div className="flex items-center gap-6 md:gap-8 flex-1 text-left">
                      <span className="font-default font-bold text-xs text-accent shrink-0 tracking-wider">
                        Q.{String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="font-default font-medium text-base md:text-lg text-foreground leading-snug">
                        {faq.question}
                      </span>
                    </div>
                    <Plus className="size-6 text-foreground shrink-0 transition-transform duration-300 group-data-[state=open]:rotate-45" strokeWidth={1.5} />
                  </AccordionTrigger>
                  <AccordionContent className="px-6 md:px-8 pb-8 pt-0">
                    <div className="pl-0 md:pl-[88px]">
                      <p className="font-default text-base md:text-[1.0625rem]" style={{
                    lineHeight: "1.75",
                    maxWidth: "600px",
                    color: "var(--muted-foreground)"
                  }}>
                        {faq.answer}
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}